import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Clock } from "lucide-react";

export default function Packages() {
  const { user, refresh } = useAuth();
  const [pkgs, setPkgs] = useState([]);
  const [pendingReq, setPendingReq] = useState(null);
  const [selected, setSelected] = useState(null);
  const [tpass, setTpass] = useState("");
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [loading, setLoading] = useState(false);

  const loadRequests = async () => {
    try {
      const r = await api.get("/me/activation-requests");
      const pending = (r.data.rows || []).find((x) => x.status === "pending");
      setPendingReq(pending || null);
    } catch (err) {
      console.warn("Activation requests load failed:", err?.message);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/packages");
        setPkgs(data.packages);
        await loadRequests();
      } catch (err) {
        console.warn("Packages load failed:", err?.message);
      }
    })();
  }, []);

  const purchase = async () => {
    if (!selected) return;
    setLoading(true); setMsg({ type: "", text: "" });
    try {
      const r = await api.post("/subscription/purchase", { package_value: selected, t_pass: tpass });
      setMsg({ type: "ok", text: r.data.message || "Request submitted to admin for activation." });
      setTpass("");
      setSelected(null);
      await refresh();
      await loadRequests();
    } catch (e) {
      setMsg({ type: "err", text: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-8">
      <div>
        <div className="overline">Subscriptions</div>
        {!user?.package_value && (
          <p className="text-amber-300/80 text-sm mt-3" data-testid="first-time-rule-note">
            First-time activation must be the <strong>Starter ($3)</strong> package. Higher tiers unlock on subsequent re-top-ups (same or higher value).
          </p>
        )}
      </div>

      {pendingReq && (
        <div
          className="lt-card p-5 border-amber-500/40 bg-amber-500/5 flex items-start gap-4"
          data-testid="pending-activation-banner"
        >
          <Clock className="text-amber-400 shrink-0 mt-0.5" size={20} />
          <div className="flex-1">
            <div className="overline text-amber-300">Pending admin approval</div>
            <div className="mt-1 text-sm">
              Your <span className="gold-text">{pendingReq.package_name}</span> package request (${pendingReq.cost.toFixed(2)}) was submitted on {new Date(pendingReq.created_at).toLocaleString()}.
            </div>
            <div className="text-xs text-neutral-400 mt-1">You'll be notified once admin activates it. Deposit is held until then.</div>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-4">
        {pkgs.map((p) => {
          const active = selected === p.value;
          const current = user?.package_value === p.value;
          const isFirstTime = !user?.package_value;
          const lockedFirstTime = isFirstTime && p.value !== 3;
          const lockedLowerTier = !isFirstTime && p.value < (user?.package_value || 0);
          const disabled = !!pendingReq || lockedFirstTime || lockedLowerTier;
          return (
            <button
              key={p.value}
              onClick={() => !disabled && setSelected(p.value)}
              disabled={disabled}
              data-testid={`package-${p.value}`}
              className={`lt-card text-left p-5 transition-all ${active ? "border-[#D4AF37]" : ""} ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              <div className="overline">{p.name}</div>
              <div className="font-display text-5xl mt-2 tabular">${p.value}</div>
              <div className="divider-gold my-4" />
              <div className="text-xs space-y-2 text-neutral-400">
                <div className="flex justify-between"><span>Earning cap</span><span className="tabular text-white">${p.earning_cap}</span></div>
                <div className="flex justify-between"><span>Renewal ×</span><span className="tabular text-white">{p.renewal_mult}x</span></div>
                <div className="flex justify-between"><span>Max cards</span><span className="tabular text-white">{p.max_cards}</span></div>
              </div>
              {current && <div className="mt-4 text-xs gold-text uppercase tracking-widest">Currently Active</div>}
              {lockedFirstTime && <div className="mt-4 text-[10px] uppercase tracking-widest text-amber-400/70">First activation must be Starter</div>}
              {lockedLowerTier && <div className="mt-4 text-[10px] uppercase tracking-widest text-neutral-500">Lower than current tier</div>}
            </button>
          );
        })}
      </div>

      {selected && !pendingReq && (
        <div className="lt-card p-6 max-w-xl" data-testid="purchase-card">
          <div className="overline mb-3">Submit ${selected} package request</div>
          <p className="text-sm text-neutral-400 mb-4">
            Total cost: <span className="gold-text tabular">${selected + 1}</span> (package + $1 Global Club entry). Debited from your Deposit balance and <strong className="text-neutral-200">held until admin approves activation</strong>.
          </p>
          <input
            type="password"
            className="lt-input"
            placeholder="Transaction password (T.Pass)"
            value={tpass}
            onChange={(e) => setTpass(e.target.value)}
            data-testid="purchase-tpass-input"
          />
          {msg.text && (
            <div className={`mt-3 text-sm ${msg.type === "ok" ? "gold-text" : "text-red-400"}`} data-testid="purchase-msg">
              {msg.text}
            </div>
          )}
          <button onClick={purchase} disabled={loading || !tpass} className="lt-btn-primary mt-4" data-testid="purchase-confirm-btn">
            {loading ? "Submitting…" : "Submit request to admin"}
          </button>
        </div>
      )}

      {msg.text && !selected && (
        <div className={`text-sm ${msg.type === "ok" ? "gold-text" : "text-red-400"}`} data-testid="purchase-result-msg">
          {msg.text}
        </div>
      )}
    </div>
  );
}
